import { action } from '@play-co/replicant';
import { profileRuleset } from './profile.ruleset';
import createActions from '../config/actions/createActions';
import { getRealLength } from '../utils/string';
import { getPlayerProfileAvatarFrame } from './profile.getters';
import { deliverFriendRequestAcceptedMessage } from '../friends/friends.chatbot';

export const profileActions = createActions({
  /**
   * Confirm user consent and update public profile
   */
  updatePublicProfile: action(
    (
      state,
      args: {
        name: string;
        photo: string;
        enteredFromInviteV2?: boolean;
        originFeature?: string;
        originSubFeature?: string;
      },
      api,
    ) => {
      const [minLength, maxLength] = profileRuleset.nameLength;

      const length = getRealLength(args.name ?? '');

      if (length < minLength || length > maxLength) {
        throw new Error(
          `Name must be between ${minLength} and ${maxLength} characters [input: '${args.name}']`,
        );
      }

      const hadPublicProfile = !!state.publicProfile;

      state.publicProfile = {
        ...(state.publicProfile ?? {}),
        name: args.name,
        photo: args.photo,
      };

      // Notify referrer that their friend is now visitable.
      if (!hadPublicProfile && state.social.referral.invitedBy) {
        api.postMessage.referralFriendVisitUnlocked(
          state.social.referral.invitedBy,
          {
            name: args.name,
            photo: args.photo,
            frame: getPlayerProfileAvatarFrame(state),
          },
        );

        // Send OA notification to inviter for new users with Add Friends V2.
        // No stored `FriendshipRequest` exists on this path, so the frontend
        // passes through attribution from the entry payload.
        const isNewUser = state.loginCount === 1;
        if (isNewUser && args.enteredFromInviteV2) {
          deliverFriendRequestAcceptedMessage(
            {
              receiverId: state.social.referral.invitedBy,
              acceptorState: state,
              now: api.date.now(),
              originFeature: args.originFeature,
              originSubFeature: args.originSubFeature,
            },
            api,
          );
        }
      }
    },
  ),

  /**
   * @warning Only for internal usage, other users should not see this information
   *
   * Update profile image and name on login automatically,
   * it will take current line photo and name
   * We can't show this information to other users !!!
   */
  updatePrivateProfile: action(
    (state, args: { name: string; photo: string }) => {
      if (!state.profile) state.profile = {};
      state.profile.name = args.name || undefined;
      state.profile.photo = args.photo || undefined;
    },
  ),
});
